/**
 * AppAnalyticsPanel - Main panel for the App Analytics plugin
 * Displays Mixpanel metrics with caching
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { PluginPanelProps } from '../_infrastructure/pluginTypes';
import {
  fetchAnalyticsSummary,
  fetchSegmentation,
  getTopDays,
  getByGeography,
  detectAnomalies,
  getTopUsers,
  generateInsights,
  answerAnalyticsQuestion,
  hasServiceAccount,
  type AnalyticsSummary,
  type MixpanelConfig,
} from './lib/mixpanel';
import { parseQuestion, toSegmentationQuery, getSuggestedFollowups } from './lib/queryParser';
import { getPluginData, savePluginData } from '../_infrastructure/pluginApi';
import { MetricCard } from './components/MetricCard';
import { Sparkline } from './components/Sparkline';
import { InsightChat } from './components/InsightChat';
import { QueryResult, type QueryResultData } from './components/QueryResult';
import type { LoadingState, CachedQueryResultPayload } from './types';
import {
  EXAMPLE_QUESTIONS,
  SUGGESTIONS_ROTATE_INTERVAL_MS,
  CACHE_DATA_TYPE,
  CACHE_EXPIRY_MINUTES,
  LAST_QUERY_RESULT_DATA_TYPE,
  SHARED_RESULT_POLL_INTERVAL_MS,
} from './constants';
import { convertCachedQueryToResult, summarizeResultForContext } from './utils/cachedQuery';

export function AppAnalyticsPanel({ instanceId, config, onOpenConfig }: PluginPanelProps) {
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [lastFetched, setLastFetched] = useState<string | null>(null);

  const [queryResult, setQueryResult] = useState<QueryResultData | null>(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const latestResultTimestampRef = useRef<string | null>(null);

  const [rotatingIndex, setRotatingIndex] = useState(0);
  const rotatingQuestions = useMemo(() => {
    const result: string[] = [];
    for (let i = 0; i < 3; i++) {
      result.push(EXAMPLE_QUESTIONS[(rotatingIndex + i) % EXAMPLE_QUESTIONS.length]);
    }
    return result;
  }, [rotatingIndex]);

  // Track data presence via ref so fetchData isn't re-created on every load.
  const hasDataRef = useRef(false);
  hasDataRef.current = data !== null;

  const isConfigured = Boolean(config.apiSecret && config.projectId);

  const hasServiceAccountFlag = Boolean((config as { hasServiceAccount?: boolean }).hasServiceAccount);
  const mixpanelConfig = useMemo<MixpanelConfig>(() => ({
    apiSecret: config.apiSecret,
    projectId: config.projectId,
    hasServiceAccount: hasServiceAccountFlag,
  }), [config.apiSecret, config.projectId, hasServiceAccountFlag]);

  const hasAdvancedQueries = hasServiceAccount(mixpanelConfig);

  const applyQueryResult = useCallback((result: QueryResultData) => {
    latestResultTimestampRef.current = result.timestamp;
    setQueryResult(result);
    setSuggestions(getSuggestedFollowups(result.type));
    setQueryError(null);
  }, [setQueryResult, setSuggestions, setQueryError]);

  // Checks cache first, then fetches fresh from Mixpanel.
  const fetchData = useCallback(async (forceRefresh = false) => {
    console.log('[AppAnalytics] fetchData called', { instanceId, isConfigured, forceRefresh });

    if (!isConfigured) {
      setLoadingState('idle');
      return;
    }

    setError(null);

    try {
      if (!forceRefresh && instanceId) {
        console.log('[AppAnalytics] Checking cache for instanceId:', instanceId);
        try {
          const cached = await getPluginData(instanceId, CACHE_DATA_TYPE);
          console.log('[AppAnalytics] Cache response:', cached ? 'found' : 'not found');
          if (cached && cached.data) {
            const expiresAt = cached.expiresAt ? new Date(cached.expiresAt) : null;
            if (!expiresAt || expiresAt > new Date()) {
              console.log('[AppAnalytics] Using cached data from', cached.fetchedAt);
              setData(cached.data as AnalyticsSummary);
              setFromCache(true);
              setLastFetched(cached.fetchedAt);
              setLoadingState('loaded');
              return;
            } else {
              console.log('[AppAnalytics] Cache expired at', expiresAt);
            }
          }
        } catch (cacheCheckErr) {
          console.warn('[AppAnalytics] Cache check failed:', cacheCheckErr);
          // Continue to fetch fresh data
        }
      } else {
        console.log('[AppAnalytics] Skipping cache check:', { forceRefresh, instanceId });
      }

      // Only show the spinner if we don't already have data to display.
      if (!hasDataRef.current) {
        setLoadingState('loading');
      }
      setFromCache(false);

      console.log('[AppAnalytics] Fetching fresh data from Mixpanel...');
      const summary = await fetchAnalyticsSummary(mixpanelConfig, 30);
      console.log('[AppAnalytics] Got summary from Mixpanel:', { dau: summary.dau, mau: summary.mau });
      setData(summary);
      setLastFetched(new Date().toISOString());
      setLoadingState('loaded');

      console.log('[AppAnalytics] About to save cache, instanceId:', instanceId);
      if (instanceId) {
        try {
          console.log('[AppAnalytics] Calling savePluginData...');
          await savePluginData(instanceId, CACHE_DATA_TYPE, summary as unknown as Record<string, unknown>, CACHE_EXPIRY_MINUTES);
          console.log('[AppAnalytics] Cache saved successfully!');
        } catch (cacheErr) {
          console.error('[AppAnalytics] Failed to cache data:', cacheErr);
        }
      } else {
        console.warn('[AppAnalytics] No instanceId - cannot save to cache!');
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
      // Only surface the error state if we have no cached data to fall back on.
      if (!hasDataRef.current) {
        setLoadingState('error');
      }
    }
  }, [isConfigured, instanceId, mixpanelConfig.apiSecret, mixpanelConfig.projectId]);

  const handleQuery = useCallback(async (question: string) => {
    if (!hasAdvancedQueries) {
      setQueryError('Service Account required for queries. Add it in plugin settings.');
      return;
    }

    if (!instanceId) {
      setQueryError('Plugin instance is not available for queries.');
      return;
    }

    setQueryLoading(true);
    setQueryError(null);

    try {
      const parsed = parseQuestion(question);
      let resultData: QueryResultData['data'];

      switch (parsed.type) {
        case 'top_days': {
          const topDays = await getTopDays(
            instanceId,
            parsed.event,
            parsed.fromDate,
            parsed.toDate,
            parsed.limit || 10
          );
          resultData = topDays;
          break;
        }

        case 'by_country':
        case 'by_city': {
          const breakdown = await getByGeography(
            instanceId,
            parsed.event,
            parsed.fromDate,
            parsed.toDate,
            parsed.dimension || 'country'
          );
          resultData = breakdown;
          break;
        }

        case 'anomaly': {
          const anomalyResult = await detectAnomalies(
            instanceId,
            parsed.event,
            parsed.fromDate,
            parsed.toDate
          );
          resultData = anomalyResult;
          break;
        }

        case 'top_users': {
          const topUsersResult = await getTopUsers(
            mixpanelConfig,
            parsed.fromDate,
            parsed.toDate,
            parsed.limit || 10
          );
          resultData = topUsersResult;
          break;
        }

        case 'trend':
        case 'compare': {
          const segQuery = toSegmentationQuery(parsed);
          const segResult = await fetchSegmentation(instanceId, segQuery);
          resultData = segResult;
          break;
        }

        case 'insight': {
          if (!data) {
            setQueryError('Please wait for analytics data to load first.');
            setQueryLoading(false);
            return;
          }
          let anomalyData;
          try {
            anomalyData = await detectAnomalies(
              instanceId,
              '$ae_session',
              parsed.fromDate,
              parsed.toDate
            );
          } catch {
            // Continue without anomaly context
          }
          const insightsResult = await generateInsights(data, anomalyData);
          resultData = insightsResult;
          break;
        }

        case 'conversation': {
          if (!data) {
            setQueryError('Please wait for analytics data to load first.');
            setQueryLoading(false);
            return;
          }
          const contextSummary = summarizeResultForContext(queryResult);
          const answer = await answerAnalyticsQuestion(data, question, { context: contextSummary });
          resultData = answer;
          break;
        }

        default: {
          setQueryError(`Query type "${parsed.type}" is not yet supported.`);
          setQueryLoading(false);
          return;
        }
      }

      applyQueryResult({
        type: parsed.type,
        question,
        data: resultData,
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      console.error('[AppAnalytics] Query error:', err);
      setQueryError(err instanceof Error ? err.message : 'Query failed');
    } finally {
      setQueryLoading(false);
    }
  }, [hasAdvancedQueries, mixpanelConfig.apiSecret, mixpanelConfig.projectId, data, instanceId, queryResult, applyQueryResult]);

  useEffect(() => {
    console.log('[AppAnalytics] Panel mounted', { instanceId, isConfigured, hasConfig: !!config });
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll for shared query results executed from the main chat to keep visuals in sync.
  useEffect(() => {
    if (!instanceId) {
      return;
    }

    let isMounted = true;
    let inFlight = false;

    const pollSharedResult = async () => {
      if (!instanceId || inFlight || !isMounted) {
        return;
      }
      inFlight = true;
      try {
        const cached = await getPluginData(instanceId, LAST_QUERY_RESULT_DATA_TYPE);
        if (!isMounted || !cached?.data) {
          return;
        }

        const converted = convertCachedQueryToResult(
          cached.data as CachedQueryResultPayload,
          cached.fetchedAt
        );
        if (!converted) {
          return;
        }

        const currentTs = latestResultTimestampRef.current
          ? new Date(latestResultTimestampRef.current).getTime()
          : 0;
        const incomingTs = converted.timestamp
          ? new Date(converted.timestamp).getTime()
          : Date.now();

        if (incomingTs <= currentTs) {
          return;
        }

        applyQueryResult(converted);
      } catch (err) {
        console.warn('[AppAnalytics] Shared query poll failed:', err);
      } finally {
        inFlight = false;
      }
    };

    pollSharedResult();
    const pollTimer = setInterval(pollSharedResult, SHARED_RESULT_POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(pollTimer);
    };
  }, [instanceId, applyQueryResult]);

  useEffect(() => {
    const timer = setInterval(() => {
      setRotatingIndex((prev) => (prev + 1) % EXAMPLE_QUESTIONS.length);
    }, SUGGESTIONS_ROTATE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, []);

  if (!isConfigured) {
    return (
      <div className="text-center py-6">
        <div className="text-3xl mb-3">📊</div>
        <p className="text-sm text-gray-500 mb-4">
          Connect your Mixpanel project to see analytics
        </p>
        <button
          onClick={onOpenConfig}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition"
        >
          Connect Mixpanel
        </button>
      </div>
    );
  }

  if (loadingState === 'loading') {
    return (
      <div className="text-center py-8">
        <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-gray-500">
          Loading analytics...
        </p>
      </div>
    );
  }

  if (loadingState === 'error') {
    return (
      <div className="text-center py-6">
        <div className="text-3xl mb-3">⚠️</div>
        <p className="text-sm text-red-600 mb-2">
          {error || 'Failed to load analytics'}
        </p>
        <div className="flex gap-2 justify-center">
          <button
            onClick={fetchData}
            className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm hover:bg-gray-200 transition"
          >
            Retry
          </button>
          <button
            onClick={onOpenConfig}
            className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 transition"
          >
            Reconfigure
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const formatRelativeTime = (isoString: string) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{data.dateRange.from} to {data.dateRange.to}</span>
        {lastFetched && (
          <span className={fromCache ? 'text-green-600' : ''}>
            {fromCache ? 'cached ' : 'fetched '}{formatRelativeTime(lastFetched)}
          </span>
        )}
      </div>

      {hasAdvancedQueries && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-3">
          <div className="text-[10px] text-blue-600 font-medium mb-2 uppercase tracking-wide">
            Try asking
          </div>
          <div className="flex flex-wrap gap-1.5">
            {rotatingQuestions.map((question) => (
              <button
                key={question}
                onClick={() => handleQuery(question)}
                disabled={queryLoading}
                className="text-xs px-2.5 py-1.5 rounded-full bg-white border border-blue-200 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-all disabled:opacity-50 shadow-sm"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <MetricCard
          label="DAU"
          value={data.dau}
          format="compact"
        />
        <MetricCard
          label="MAU"
          value={data.mau}
          format="compact"
        />
        <MetricCard
          label="Stickiness"
          value={data.stickiness}
          format="percent"
        />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard
          label="D1 Retention"
          value={data.retentionD1}
          format="percent"
          size="sm"
        />
        <MetricCard
          label="D7 Retention"
          value={data.retentionD7}
          format="percent"
          size="sm"
        />
        <MetricCard
          label="D30 Retention"
          value={data.retentionD30}
          format="percent"
          size="sm"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
        <Sparkline
          data={data.dailyActiveUsers}
          label="Daily Active Users"
          color="var(--color-primary)"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
        <div className="text-xs text-gray-500 mb-2">Top Events</div>
        <div className="space-y-1">
          {data.topEvents.slice(0, 5).map((event, i) => (
            <div key={event.name} className="flex items-center justify-between text-xs">
              <span className="text-gray-700 truncate flex-1">
                {i + 1}. {event.name}
              </span>
              <span className="text-gray-500 ml-2">
                {event.count.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
        <div className="text-xs text-gray-500 mb-2">Platforms</div>
        <div className="flex flex-wrap gap-2">
          {data.platforms.slice(0, 4).map((platform) => {
            const total = data.platforms.reduce((sum, p) => sum + p.count, 0);
            const pct = Math.round((platform.count / total) * 100);
            return (
              <div
                key={platform.name}
                className="px-2 py-1 rounded bg-gray-50 border border-gray-200 text-xs"
              >
                <span className="text-gray-700">{platform.name}</span>
                <span className="text-gray-500 ml-1">{pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {queryResult && (
        <QueryResult
          result={queryResult}
          onDismiss={() => setQueryResult(null)}
        />
      )}

      {queryError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
          {queryError}
          <button
            onClick={() => setQueryError(null)}
            className="ml-2 text-red-400 hover:text-red-600"
          >
            ×
          </button>
        </div>
      )}

      {hasAdvancedQueries ? (
        <InsightChat
          onAsk={handleQuery}
          isLoading={queryLoading}
          suggestions={suggestions.length > 0 ? suggestions : [
            'Top 10 days for sessions',
            'Sessions by country',
            'Any anomalies?',
          ]}
        />
      ) : (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-3">
          <div className="flex items-start gap-2">
            <span className="text-purple-500 text-sm">✨</span>
            <div className="flex-1">
              <p className="text-xs font-medium text-purple-800 mb-1">
                Unlock Advanced Queries
              </p>
              <p className="text-xs text-purple-700 mb-2">
                Add a Service Account to ask questions like:
              </p>
              <ul className="text-xs text-purple-600 space-y-0.5 mb-2">
                <li>• "Top 10 days for sessions this year"</li>
                <li>• "Sessions by country"</li>
                <li>• "Any anomalies in the last 90 days?"</li>
                <li>• "Give me actionable insights"</li>
              </ul>
              <button
                onClick={onOpenConfig}
                className="text-xs font-medium text-purple-700 hover:text-purple-900 underline"
              >
                Configure Service Account →
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
